import React, { useState } from "react";
import { FiFilter } from "react-icons/fi";
import "./MyCharters.css"; // Reusing the MyCharters styles

export default function ActiveVessels() {
  // Sample data for demonstration
  const charterData = [
    {
      id: 1,
      vesselName: "ASD Tug Navigator",
      vesselType: "AHTS 001",
      image: "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png",
      specifications: {
        bollardPull: "48T Bollard Pull",
        paxAccommodation: "18 Pax Accommodation",
        deckSpace: "125 m² Deck Space",
        deckStrength: "577/m² Deck Strength",
        bhp: "4,200 BHP",
        classification: "Classification: ABS"
      },
      route: "Tincan port - Apapa port",
      duration: "20 Days",
      price: "$2000/Day",
      startDate: "2024-09-15",
      endDate: "2024-10-05",
      status: "Active",
      remainingDays: 12,
      fuelConsumption: "15 tons/day",
      captain: "Capt. James Wilson",
      maintenanceDate: "2024-11-10",
      operationalStatus: "Optimal",
      lastInspection: "2024-08-20"
    },
    {
      id: 4,
      vesselName: "Supply Vessel Emma",
      vesselType: "PSV 005",
      image: "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png",
      specifications: {
        bollardPull: "52T Bollard Pull",
        paxAccommodation: "24 Pax Accommodation",
        deckSpace: "150 m² Deck Space",
        deckStrength: "600/m² Deck Strength",
        bhp: "5,000 BHP",
        classification: "Classification: ABS"
      },
      route: "Lagos - Escravos",
      duration: "45 Days",
      price: "$2500/Day",
      startDate: "2024-08-20",
      endDate: "2024-10-05",
      status: "Active",
      remainingDays: 25,
      fuelConsumption: "18 tons/day",
      captain: "Capt. Sarah Johnson",
      maintenanceDate: "2024-10-25",
      operationalStatus: "Maintenance Due",
      lastInspection: "2024-07-15"
    }
  ];

  // State for expanded vessel details
  const [expandedId, setExpandedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("remainingDays");
  const [sortOrder, setSortOrder] = useState("asc");

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter types based on unique vessel types
  const vesselTypes = ["All", ...new Set(charterData.map(vessel => vessel.vesselType))];

  // Sort and filter data
  const filteredAndSortedData = charterData
    .filter(vessel => activeFilter === "All" || vessel.vesselType === activeFilter)
    .sort((a, b) => {
      const sortValue = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "remainingDays") {
        return (a.remainingDays - b.remainingDays) * sortValue;
      }
      if (sortBy === "price") {
        return (parseInt(a.price.replace(/\D/g, "")) - parseInt(b.price.replace(/\D/g, ""))) * sortValue;
      }
      return 0;
    });

  return (
    <div className="my-charters-container">
      <div className="my-charters-header">
        <h2 className="my-charters-section-title">Active Vessels ({filteredAndSortedData.length})</h2>
        
        <div className="my-charters-filters">
          <div className="filter-dropdown">
            <div className="filter-icon">
              <FiFilter />
              <span>Filter by:</span>
            </div>
          </div>
          
          <div className="vessel-type-filters">
            {vesselTypes.map(type => (
              <button
                key={type}
                className={`vessel-type-button ${activeFilter === type ? 'active' : ''}`}
                onClick={() => setActiveFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>
          
          <div className="sort-controls">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="remainingDays">Sort by Remaining Days</option>
              <option value="price">Sort by Price</option>
            </select>
            <button 
              className="sort-order-button"
              onClick={() => setSortOrder(order => order === "asc" ? "desc" : "asc")}
            >
              {sortOrder === "asc" ? "↑" : "↓"}
            </button>
          </div>
        </div>
      </div>

      <div className="my-charters-list">
        {filteredAndSortedData.map(charter => (
          <div className={`charter-card ${expandedId === charter.id ? 'expanded' : ''}`} key={charter.id}>
            <div className="charter-image">
              <img src={charter.image} alt={charter.vesselName} />
              <div className="charter-image-overlay">
                <span className="vatug-status-badge active">Active</span>
                <span className="remaining-days">{charter.remainingDays} days left</span>
              </div>
            </div>
            
            <div className="charter-content">
              <div className="charter-header">
                <div className="charter-title">
                  <h3>{charter.vesselName}</h3>
                  <span className="charter-type">{charter.vesselType}</span>
                </div>
                <div className="charter-price-container">
                  <div className="charter-price">{charter.price}</div>
                  <span className="price-duration">({charter.duration})</span>
                </div>
              </div>
              
              <div className="charter-details">
                <div className="charter-specifications">
                  <h4>Specifications</h4>
                  <ul>
                    {Object.values(charter.specifications).map((spec, index) => (
                      <li key={index}>{spec}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="charter-booking-details">
                  <div className="booking-detail">
                    <span className="detail-label">Route:</span>
                    <span className="detail-value">{charter.route}</span>
                  </div>
                  <div className="booking-detail">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">{charter.duration}</span>
                  </div>
                  <div className="booking-detail">
                    <span className="detail-label">Remaining:</span>
                    <span className="detail-value highlight">{charter.remainingDays} days</span>
                  </div>
                  <div className="booking-detail">
                    <span className="detail-label">Status:</span>
                    <span className={`vatug-status-badge ${charter.operationalStatus === "Optimal" ? "active" : "maintenance"}`}>
                      {charter.operationalStatus}
                    </span>
                  </div>
                </div>
              </div>
              
              {expandedId === charter.id && (
                <div className="charter-expanded-details">
                  <div className="expanded-section">
                    <h4>Operation Details</h4>
                    <div className="expanded-grid">
                      <div className="expanded-detail">
                        <span className="detail-label">Start Date:</span>
                        <span className="detail-value">{charter.startDate}</span>
                      </div>
                      <div className="expanded-detail">
                        <span className="detail-label">End Date:</span>
                        <span className="detail-value">{charter.endDate}</span>
                      </div>
                      <div className="expanded-detail">
                        <span className="detail-label">Fuel Usage:</span>
                        <span className="detail-value">{charter.fuelConsumption}</span>
                      </div>
                      <div className="expanded-detail">
                        <span className="detail-label">Captain:</span>
                        <span className="detail-value">{charter.captain}</span>
                      </div>
                      <div className="expanded-detail">
                        <span className="detail-label">Next Maintenance:</span>
                        <span className="detail-value">{charter.maintenanceDate}</span>
                      </div>
                      <div className="expanded-detail">
                        <span className="detail-label">Last Inspection:</span>
                        <span className="detail-value">{charter.lastInspection}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="charter-actions">
                <button className="view-details-btn" onClick={() => toggleExpand(charter.id)}>
                  {expandedId === charter.id ? 'Hide Details' : 'View Details'}
                </button>
                <button className="manage-charter-btn">Manage Charter</button>
                <button className="extend-charter-btn">Extend Charter</button>
                <button className="log-issue-btn">Log Issue</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 